import $ from 'jquery';

import { Skin } from 'lib/vendor/terrific/Application';

import { hashDeepLink } from 'lib/util/url';

import LegoContainer, {
	DISABLED_VALUES,
	MOBILE_ABO_DEVICE_TOGGLE,
	ServiceId,
	toFloat,
} from '../Tc.Module.LegoContainer';
// tslint:disable-next-line: no-duplicate-imports
import type {
	LegoContainerConfig,
	CurrentSelection,
	Flavour,
	Service,
	TrackingInfo,
	ConfigState,
} from '../Tc.Module.LegoContainer';

import type { FlavourAvailability } from '../Tc.Module.LegoOrderButton';

export interface SliderData {
	sliderId : string;
	slideId : string;
	index : number;
}

interface ConfiguratorSkin extends LegoContainer {
	allSlidesInitialized : boolean;
	dataLoaded : boolean;
	trackUserInteractionWithSlider : boolean;

	setInitialDisabledState(
		updateConfig : LegoContainerConfig[],
		extendConfig : Record<keyof CurrentSelection, LegoContainerConfig>,
		forceOverride? : boolean,
	) : LegoContainerConfig[];
	getCheckboxDefaultState() : Record<keyof CurrentSelection, LegoContainerConfig>;
	handleCheckbox(event : JQueryMouseEventObject) : void;
	handleSliderInteraction() : void;
	handlePurchaseMobileDevice() : void;
	updateUI(data : LegoContainerConfig[]) : void;
	trackCheckbox(siebelId : string, checked : boolean) : void;
	setInitialState() : void;
	updateFlavourPrices(data : Service, activeSlideId : string) : void;
	preparePriceDifferences(flavours : Flavour[], slideId : string) : Record<string, string>;
	trackInitialConfiguration(deepLinks : CurrentSelection | LegoContainerConfig[]) : void;
	onSliderSlideInitialized(data : SliderData) : void;
	onSliderSlideChanged(data : SliderData) : void;
	onFlavourAvailability(data : FlavourAvailability) : void;
}

// tslint:disable-next-line: variable-name
export const Configurator : Skin<LegoContainer, ConfiguratorSkin> = function Configurator(parent) {
	this.initComponent = function() {
		parent.initComponent.call(this);
		this.sandbox.subscribe('SLIDER', this);
		this.allSlidesInitialized = false;
		this.dataLoaded = false;
		this.trackUserInteractionWithSlider = false;
	};

	this.addEventListeners = function() {
		parent.addEventListeners.call(this);

		this.ui.bundleLinks.on('click', this.handleBundleLink.bind(this));
		this.ui.checkboxes.on('change', this.handleCheckbox.bind(this));
		this.ui.slider.on('mousedown touchstart', this.handleSliderInteraction.bind(this));
		this.$ctx.find('.bx-controls a').on('mousedown', this.handleSliderInteraction.bind(this));
		this.ui.purchaseMobileDeviceToggle.on('change', this.handlePurchaseMobileDevice.bind(this));
	};

	this.cacheUi = function() {
		parent.cacheUi.call(this);
		this.ui.slider = this.$ctx.find('.mod.modSlider');
		this.ui.slides = this.$ctx.find('.modSlide');
		this.ui.purchaseMobileDeviceToggle = this.$ctx.find(`input[name="${MOBILE_ABO_DEVICE_TOGGLE}"]`);

		this.ui.slides.find('p:contains("${data.priceDifference}")').addClass('priceContainer').toggleClass('invisible', !this.isEditMode);
	};

	this.setInitialDisabledState = function(updateConfig, extendConfig, forceOverride = false) {
		$.each(extendConfig, (extKey, extConf) => {
			let extend = true;
			$.each(updateConfig, (index, updateConf) => {
				if(extKey === updateConf.sliderId) {
					if(forceOverride) {
						this.disabledSliderState[extKey] = updateConfig[index].slideId;
						updateConfig[index] = extConf;
					}
					extend = false;

					return false;
				}

				return undefined;
			});

			if(extend) {
				updateConfig.push(extConf);
				if(this.currentSelection[extKey]) {
					this.disabledSliderState[extKey as ServiceId] = this.currentSelection[extKey]!.slideId;
				}
			}
		});

		return updateConfig;
	};

	this.getCheckboxDefaultState = function() {
		const checkboxConfig = {} as any as Record<keyof CurrentSelection, LegoContainerConfig>;
		const mod = this;
		mod.ui.checkboxes.each((_i, checkbox) => {
			const $checkbox = $(checkbox);

			if($checkbox.prop('checked')) {
				const sliderId =  $checkbox.val() as ServiceId;
				const slideId = DISABLED_VALUES[$checkbox.prop('name') as keyof typeof DISABLED_VALUES];
				checkboxConfig[sliderId] = {
					cost: this.getFlavourPrice(sliderId, slideId),
					slideId,
					sliderId,
				};
			}
		});

		return checkboxConfig;
	};

	this.handleCheckbox = function(event) {
		const checkbox = $(event.currentTarget);
		const sliderId = checkbox.val() as ServiceId;
		const checked = checkbox.prop('checked');
		const siebelId = DISABLED_VALUES[checkbox.prop('name') as keyof typeof DISABLED_VALUES];

		if (checked) {
			this.disabledSliderState[sliderId] = this.currentSelection[sliderId]!.slideId;
			const data = {
				cost: this.getFlavourPrice(sliderId, siebelId),
				slideId: siebelId,
				sliderId,
			};
			this.updateUI([data]);
			this.broadcastConfigurationChanged(this.currentSelection, this.calculateTotalPrice([data]));
			this.fire('sliderToggleEnable', {sliderId, enable: false}, ['SLIDER']); // Tc.Module.Slider.RotatingTeaser
			this.trackCheckbox(siebelId, checked);
		} else if(!checkbox.prop('checked') && this.disabledSliderState[sliderId]) {
			const data = {
				cost: 0,
				slideId: this.disabledSliderState[sliderId]!,
				sliderId,
			};
			this.updateUI([data]);
			this.broadcastConfigurationChanged(this.currentSelection, this.calculateTotalPrice([data]));
			this.disabledSliderState[sliderId] = '';
			this.fire('sliderToggleEnable', {sliderId, enable: true}, ['SLIDER']);
			this.trackCheckbox(siebelId, checked);
		}

		this.isBundleConfiguration();
	};

	this.handleDataSuccess = function() {
		this.dataLoaded = true;

		if(this.allSlidesInitialized) {
			this.setInitialState();
		}
	};

	this.handlePurchaseMobileDevice = function() {
		this.options[MOBILE_ABO_DEVICE_TOGGLE] = this.ui.purchaseMobileDeviceToggle.filter(':checked').val() as string;
		this.toggleMobilePrices(this.options[MOBILE_ABO_DEVICE_TOGGLE]);
		this.updateSummaryUi(this.currentSelection);
		this.broadcastConfigurationChanged(
			this.currentSelection,
			this.calculateTotalPrice(this.getUpdateDataFromCurrentSelection()),
		);

		if(this.config && this.config.mobile && this.currentSelection.mobile && this.currentSelection.mobile.slideId) {
			this.updateFlavourPrices(this.config.mobile, this.currentSelection.mobile.slideId);
		}
	};

	this.handleSliderInteraction = function() {
		this.trackUserInteractionWithSlider = true;
	};

	function getSelectedPrice(flavours : Flavour[], slideId : string) {
		for(const flavour of flavours) {
			if (flavour.siebelId === slideId) {
				return toFloat(flavour.priceDifference);
			}
		}

		return null;
	}

	this.preparePriceDifferences = function(flavours, slideId) {
		const differences : Record<string, string> = {};
		const selectedPrice = getSelectedPrice(flavours, slideId);

		for (const flavour of flavours) {
			if (flavour.siebelId === slideId) {
				differences[flavour.siebelId] = this.i18n.selected;
				continue;
			}

			const price = toFloat(flavour.priceDifference) - (selectedPrice ?? 0);
			const prefix = (price > 0) ? '+' : '';

			differences[flavour.siebelId] = prefix + SCS.utils.formatPriceWithDash(price) + this.i18n.monthShort;
		}

		return differences;
	};

	this.setInitialState = function() {
		const checkboxStates = this.getCheckboxDefaultState();
		const configString = hashDeepLink.get('lpIds') || this.readFromStorage();
		const initConf = this.deserializeDeeplink(configString);

		if(initConf && initConf.config.length) {
			initConf.config = this.setInitialDisabledState(initConf.config, checkboxStates);
			this.preselectSliderPositions(initConf.config);
			this.trackInitialConfiguration(initConf.config);
			if(!Object.keys(initConf.options).length) {
				this.setInitialOptions(this.options);
			} else {
				this.setInitialOptions(initConf.options);
			}
			this.initComplete = true;
			this.updateUI(initConf.config);
			this.broadcastConfigurationChanged(this.currentSelection, this.calculateTotalPrice(initConf.config));
		} else {
			let updateData = this.getUpdateDataFromCurrentSelection();
			updateData = this.setInitialDisabledState(updateData, checkboxStates, true);
			this.setInitialOptions(this.options);
			this.initComplete = true;
			this.updateUI(updateData);
			this.broadcastConfigurationChanged(this.currentSelection, this.calculateTotalPrice(updateData));
			this.trackInitialConfiguration(updateData);
		}
	};

	this.trackInitialConfiguration = function(deepLinks) {
		const mod = this;

		const trackingInfo : TrackingInfo = {
			conversionCategory: 'offer',
			conversionClass: this.getLocationString(),
			conversionDetail: 'preselect',
			selectedProducts: deepLinks,
		};

		setTimeout(function() {
			mod.track(trackingInfo);
		}, 500);
	};

	this.trackCheckbox = function(checkboxValue, checked) {
		if(this.allSlidesInitialized && this.dataLoaded) {
			const trackingInfo : TrackingInfo = {
				action: 'select',
				category: 'optionchange',
				label: checkboxValue + ((checked) ? '_ON' : '_OFF'),
				location: this.getLocationString(),
				selectedProducts: [{slideId: checkboxValue} as any as LegoContainerConfig],
			};

			this.track(trackingInfo);
		}
	};

	this.updateFlavourPrices = function(data, activeSlideId) {
		if (data && data.flavours) {
			const mod = this;
			const priceText = mod.preparePriceDifferences(data.flavours, activeSlideId);

			$.each(data.flavours, function(_i, flavour) {
				const slide = mod.ui.slides.filter(`[data-id="${flavour.siebelId}"]`);
				slide.find('.priceContainer').text(priceText[flavour.siebelId]).removeClass('invisible');
			});
		}
	};

	this.updateMobileDeviceOption = function(newValue) {
		parent.updateMobileDeviceOption.call(this, newValue);

		this.ui.purchaseMobileDeviceToggle.filter(`[value="${newValue}"]`).prop('checked', true);
	};

	this.updateUI = function(data) {
		if(!this.allSlidesInitialized || !this.dataLoaded) {
			return;
		}

		$.each(data, (_i, item) => {
			this.updateFlavourPrices(this.config![item.sliderId]!, item.slideId);
		});

		this.updateSummaryUi(data);
		this.isBundleConfiguration();
	};

	/**
	 * Terrific Handler, fired by Tc.Module.Slider
	 */
	this.onSliderSlideInitialized = function(data) {
		this.slidersInitialized += 1;
		this.allSlidesInitialized = this.slidersInitialized === this.ui.slider.length;
		this.onSliderSlideChanged(data);

		// Wait until all sliders on the page have finished initializing.
		if(this.allSlidesInitialized && this.dataLoaded) {
			this.setInitialState();
		}
	};

	/**
	 * Terrific Handler, fired by Tc.Module.Slider
	 */
	this.onSliderSlideChanged = function(data) {
		const mod = this;
		mod.updateUI([data as any as LegoContainerConfig]);
		mod.broadcastConfigurationChanged(mod.currentSelection, mod.calculateTotalPrice([data as ConfigState]));

		if(mod.trackUserInteractionWithSlider) {
			mod.trackUserInteractionWithSlider = false;

			const trackingInfo : TrackingInfo = {
				action: 'select',
				category: 'optionchange',
				label: data.slideId,
				location: mod.getLocationString(),
				selectedProducts: [{slideId: data.slideId} as any as LegoContainerConfig],
			};

			mod.track(trackingInfo);
		}
	};

	/**
	 * Terrific Handler, fired by Tc.Module.LegoOrderButton
	 */
	this.onFlavourAvailability = function(_data) {
		// This is a hook to enable/disable features in the configurator.
		// So far this was not implemented.
	};
};



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/lego/skins/Configurator.ts